import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Card,
    Typography,
    LinearProgress,
    Stack,
    Chip,
} from "@mui/material";
import Images from "../../utils/Images";
import { API } from '../../utils/Api';
import axios from "axios";
import JourneyCouponModal from "./JourneyCouponModal";

const ActiveJourneyCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const token = localStorage.getItem('token');

    const getCoupons = async () => {
        try {
            const response = await axios.get(`${API.BASE_URL_2}coupons/user-journey-coupons`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setCoupons(response?.data?.data?.journeyCoupons || []);
        } catch (error) {
            console.log("Error in getting Coupons", error?.response);
        }
    };

    useEffect(() => {
        getCoupons();
    }, []);

    const displayList = useMemo(() => {
        if (!coupons?.length) return [];

        const sorted = [...coupons].sort((a, b) => {
            const aEnd = new Date(a?.coupon?.validityEndDate || 0).getTime();
            const bEnd = new Date(b?.coupon?.validityEndDate || 0).getTime();
            return aEnd - bEnd;
        });

        const list = sorted.map((c) => ({
            type: "ACTIVE",
            id: c._id,
            raw: c,
        }));

        if (list.length % 2 === 1) {
            list.push({
                type: "LOCKED",
                id: "locked-filler",
                label: "XPANSE COUPON",
                message: "UNLOCK OFFER\nON YOUR NEXT\nORDER",
            });
        }

        return list;
    }, [coupons]);

    if (!displayList.length) return null;

    return (
        <Grid container spacing={1} justifyContent="center" alignItems="stretch">
            {displayList.map((item) => (
                <Grid key={item.id} mb={2} item xs={6} display="flex">
                    {item.type === "ACTIVE" ? (
                        <ActiveCouponCard coupon={item.raw} />
                    ) : (
                        <LockedCouponCard coupon={item} />
                    )}
                </Grid>
            ))}
        </Grid>
    );
};

export default ActiveJourneyCoupons;

const ActiveCouponCard = ({ coupon, onSelect }) => {
    const [now, setNow] = useState(Date.now());
    const [open, setOpen] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    const startMs = useMemo(() => {
        const s = coupon?.coupon?.validityStartDate;
        return s ? new Date(s).getTime() : 0;
    }, [coupon]);

    const expiryMs = useMemo(() => {
        const end = coupon?.coupon?.validityEndDate;
        return end ? new Date(end).getTime() : 0;
    }, [coupon]);

    const remainingMs = Math.max(0, expiryMs - now);

    const timerText = useMemo(() => {
        const totalSec = Math.floor(remainingMs / 1000);
        const daySec = 24 * 60 * 60;

        if (totalSec >= daySec) {
            const days = Math.floor(totalSec / daySec);
            return `${days} day${days > 1 ? "s" : ""}`;
        }

        const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
        const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
        const ss = String(totalSec % 60).padStart(2, "0");
        return `${hh}:${mm}:${ss}`;
    }, [remainingMs]);

    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    const progress = useMemo(() => {
        if (!startMs || !expiryMs || expiryMs <= startMs) return 0;

        const total = expiryMs - startMs;
        const used = Math.min(Math.max(now - startMs, 0), total);
        const pct = (used / total) * 100;

        return Math.min(100, Math.max(0, pct));
    }, [startMs, expiryMs, now]);

    const handleClick = () => {
        console.log("Selected Coupon:", coupon);
        setSelectedCoupon(coupon);
        setOpen(true);
        if (onSelect) onSelect(coupon);
    };

    return (
        <>
            <Card
                onClick={handleClick}
                elevation={0}
                sx={{
                    cursor: "pointer",
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: "rgba(0,0,0,0.08)",
                    p: 1,
                    pb: 0,
                    bgcolor: "#fff",
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                    },
                }}
            >
                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        position: "relative",
                        bgcolor: "rgba(0,0,0,0.03)",
                    }}
                >
                    <img
                        style={{ height: "100%", width: "100%", objectFit: "contain" }}
                        src={
                            coupon?.autoCouponAllocation?.displayImageUrl ||
                            Images.sizeUpModal
                        }
                        alt="coupon"
                    />
                </Box>

                <Stack spacing={1.2} sx={{ mt: 0.7, px: 0.5 }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "rgba(0,0,0,0.7)",
                                fontWeight: 400,
                            }}
                        >
                            Expires in
                        </Typography>

                        <Chip
                            label={timerText}
                            sx={{
                                borderRadius: 1.2,
                                fontWeight: 800,
                                fontSize: 13,
                                height: 25,
                                bgcolor: "#FCEC23",
                                color: "#111",
                                "& .MuiChip-label": { px: 1.2, py: 0 },
                            }}
                        />
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 12,
                            borderRadius: 999,
                            bgcolor: "rgba(25, 118, 210, 0.12)",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 999,
                                bgcolor: "#2BB7FF",
                                transition: "none",
                            },
                        }}
                    />
                </Stack>
            </Card>
            <JourneyCouponModal
                open={open}
                onClose={() => setOpen(false)}
                data={selectedCoupon}
            />
        </>
    );
};

const LockedCouponCard = ({ coupon }) => {
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                p: 1,
                pt: 0,
                m: 0,
                pb: 0,
                bgcolor: "#fff",
                height: '100%',
                width: '100%',
                display: "flex",
                flex: 1,
                flexDirection: "column",
            }}
        >
            <Box
                component="img"
                src={Images.unlockJourney}
                alt="coupon-artwork"
                sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    p: 0,
                    m: 0,
                    pt: 1,
                    bg: 'red',
                    borderRadius: '10px'
                }}
            />

            <Typography
                sx={{
                    mt: 1,
                    mb: 1.5,
                    textAlign: "center",
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    color: "#09A5F7",
                    fontSize: 13,
                }}
            >
                {coupon.label || "XPANSE COUPON"}
            </Typography>
        </Card>
    );
};
